import { WorkPage, WorkSection, WorkEmpty } from '../../components/work-page.tsx';
import '../portal-page.css';
import { TableScroll } from '@web/components/table-scroll.tsx';
import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, AdminApiError, type AdminListEntry } from '../../api/admin.ts';
import { Alert, Button, LoadingBlock, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { useAdminMe } from '../../components/admin/admin-layout.tsx';

/**
 * The allowlist: who can sign into this console. Rows are deactivated rather
 * than deleted, so the audit trail keeps naming real people and a removal is
 * reversible.
 */
export default function AdminAdminsPage() {
  useDocumentTitle('Admins');
  const me = useAdminMe();
  const queryClient = useQueryClient();

  const admins = useQuery({ queryKey: ['admin', 'admins'], queryFn: ({ signal }) => adminApi.listAdmins(signal) });

  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
  }

  async function handleAdd(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await adminApi.addAdmin(email.trim());
      setNotice(`${email.trim()} can now sign in with Google.`);
      setEmail('');
      await refresh();
    } catch (caught) {
      setError(caught instanceof AdminApiError ? caught.message : 'The admin could not be added.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(entry: AdminListEntry) {
    if (!window.confirm(`Remove admin access for ${entry.email}?`)) return;
    setError(null);
    setNotice(null);
    try {
      await adminApi.removeAdmin(entry.id);
      await refresh();
    } catch (caught) {
      setError(caught instanceof AdminApiError ? caught.message : 'The admin could not be removed.');
    }
  }

  async function handleReactivate(entry: AdminListEntry) {
    setError(null);
    setNotice(null);
    try {
      await adminApi.reactivateAdmin(entry.id);
      await refresh();
    } catch (caught) {
      setError(caught instanceof AdminApiError ? caught.message : 'The admin could not be reactivated.');
    }
  }

  return (
    <div className="portal-page">
      <WorkPage
        title="Admins"
        description="Google accounts allowed into this console. Access is the list — there are no passwords here."
      >
        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}
        {notice ? (
          <div className="mb-4">
            <Alert tone="success">{notice}</Alert>
          </div>
        ) : null}

        <WorkSection title="Add an operator">
          <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-3" noValidate>
            <div className="min-w-64 flex-1">
              <TextInput
                label="Google account email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="operator@example.com"
              />
            </div>
            <div className="pb-4">
              <Button type="submit" variant="primary" busy={busy} disabled={!email.trim()}>
                Add admin
              </Button>
            </div>
          </form>
        </WorkSection>

        {admins.isLoading ? (
          <LoadingBlock label="Loading admins…" />
        ) : admins.isError ? (
          <Alert tone="error">
            The admin list could not be loaded.{' '}
            <Button variant="ghost" onClick={() => void admins.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : admins.data?.length === 0 ? (
          <WorkEmpty title="No admins on record" />
        ) : (
          <TableScroll aria-label="Admins" className="card overflow-x-auto p-0">
            {/* The wrapper scrolls, but only if the table may be wider than it.
              On `w-full` alone a phone squeezes four columns into 390px and
              every cell wraps to one character per line — a status badge
              reading downwards. The table asks for the width its content
              needs instead, and the columns that must never break say so. */}
            <table className="data-table w-full min-w-max text-sm">
              <thead>
                <tr className="border-b border-[color:var(--color-line)] text-left text-[color:var(--color-muted)]">
                  <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                    Email
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                    Status
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium whitespace-nowrap">
                    Added
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium" />
                </tr>
              </thead>
              <tbody>
                {(admins.data ?? []).map((entry) => (
                  <tr key={entry.id} className="border-b border-[color:var(--color-line)] last:border-b-0">
                    <td className="px-4 py-3 font-medium whitespace-nowrap">
                      {entry.email}
                      {entry.email === me.data?.email ? (
                        <span className="ml-2 text-xs text-[color:var(--color-muted)]">(you)</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {!entry.isActive ? (
                        <span className="badge border-[color:var(--color-line)] text-[color:var(--color-muted)]">
                          Removed
                        </span>
                      ) : entry.hasSignedIn ? (
                        <span className="badge border-[color:var(--color-brand)] text-[color:var(--color-brand)]">
                          Active
                        </span>
                      ) : (
                        <span className="badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
                          Invited
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-[color:var(--color-muted)]">
                      {new Date(entry.createdAt).toLocaleDateString()}
                      {entry.createdBy ? ` by ${entry.createdBy}` : ''}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {entry.isActive ? (
                        entry.email === me.data?.email ? null : (
                          <Button variant="ghost" onClick={() => void handleRemove(entry)}>
                            Remove
                          </Button>
                        )
                      ) : (
                        <Button variant="ghost" onClick={() => void handleReactivate(entry)}>
                          Reactivate
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </WorkPage>
    </div>
  );
}
